import { useProductInformationQuery } from "@src/components/plan/queries/product-information-query";
import { planQueryKeys } from "@src/components/plan/queries/query-keys";
import { StripeCheckoutModal } from "@src/components/plan/stripe-checkout-modal";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useRedeemReward } from "../../mutations/redeem-reward";
import {
	type Reward,
	referralProfileQueryKeys,
} from "../../queries/get-my-referral-profile";
import { formatRewardExpiryDate } from "../helpers";

export function RewardCodeCard({ reward }: { reward: Reward }) {
	const [checkoutModalOpened, setCheckoutModalOpened] = useState(false);
	const { data: productInfo } = useProductInformationQuery();
	const { proMonthly } = productInfo.priceIds;
	const queryClient = useQueryClient();

	const { mutateAsync: claimReward, isPending: isRedeeming } = useRedeemReward(
		async (result) => {
			if (!result?.needPaymentMethod || !proMonthly) {
				return;
			}
			setCheckoutModalOpened(true);
		},
	);

	const isClaiming = isRedeeming || checkoutModalOpened;

	const handleClaimReward = useCallback(async () => {
		await claimReward({
			rewardCode: reward.rewardCode,
		}).catch(() => undefined);
	}, [claimReward, reward.rewardCode]);

	const handleCheckoutClose = useCallback(async () => {
		setCheckoutModalOpened(false);
		await queryClient.invalidateQueries({
			queryKey: referralProfileQueryKeys.all,
		});
		await queryClient.invalidateQueries({
			queryKey: planQueryKeys.myPlan(),
		});
	}, [queryClient]);

	if (reward.isRedeemed) {
		return (
			<div className="flex items-center justify-between px-4 py-3 rounded-lg border border-border-subtle bg-surface-subtle opacity-60">
				<div className="flex-1">
					<div className="font-mono text-sm font-semibold text-text-muted line-through">
						{reward.rewardCode}
					</div>
					<div className="text-[11px] text-text-muted mt-0.5">
						Expires {formatRewardExpiryDate(reward.expiresAt)}
					</div>
				</div>
				<span className="text-[10px] font-semibold px-2 py-1 rounded bg-surface-hover-muted text-text-secondary">
					Redeemed
				</span>
			</div>
		);
	}

	return (
		<>
			<div className="flex items-center justify-between px-4 py-3 rounded-lg border border-border-default bg-surface-subtle">
				<div className="flex-1">
					<div className="font-mono text-sm font-semibold text-text-primary">
						{reward.rewardCode}
					</div>
					<div className="text-[11px] text-text-secondary mt-0.5">
						Expires {formatRewardExpiryDate(reward.expiresAt)}
					</div>
				</div>
				<button
					type="button"
					onClick={handleClaimReward}
					disabled={isClaiming}
					className="px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
					aria-label={`Claim reward ${reward.rewardCode}`}
				>
					{isClaiming ? "Claiming..." : "Claim"}
				</button>
			</div>

			{proMonthly && checkoutModalOpened && (
				<StripeCheckoutModal
					opened={checkoutModalOpened}
					onClose={handleCheckoutClose}
					priceId={proMonthly}
					discountCode={reward.rewardCode}
					zIndex={6000}
				/>
			)}
		</>
	);
}
